import { customElement, query } from "lit/decorators.js";
import { SlhCoreAvatar, Sizes } from "@slhuicore/avatar";
import { html, LitElement, TemplateResult } from "lit";
import { IconDefaultType as IconType } from "@slhuiicon/icon";
import ColorHash from "color-hash";
import { SizeValues } from "./avatar.type";
import { style } from "./assets/avatar.generated-style";

/**
 * @customElement slh-avatar
 *
 * @cssprop --slh-color-primary-shade-40 - Avatar background color.
 * @cssprop --slh-default-font-family - Avatar font family.
 * @cssprop --slh-base-xxl-font-size - Avatar XL font size.
 * @cssprop --slh-base-l-font-size - Avatar LG font size.
 * @cssprop --slh-base-xs-font-size - Avatar MD font size.
 * @cssprop --slh-base-xxs-font-size - Avatar SM font size.
 * @cssprop --slh-base-bold-font-weight - Avatar font weight.
 * @cssprop --slh-color-neutral-shade-40 - Avatar icon color.
 * @cssprop --slh-color-neutral-shade-00 - Avatar font color.
 */
@customElement("slh-avatar")
export default class SlhAvatar extends SlhCoreAvatar(LitElement) {
  @query(".avatar", false)
  avatar: HTMLElement | undefined;

  static styles = style;

  protected updated(): void {
    if (this.avatar) {
      this.avatar.style.backgroundColor = this.getFullNameColor();
    }
  }

  /**
   * @returns render icon
   */
  renderIcon(): TemplateResult {
    const iconSize = this.getIconSize();
    return html`<slh-icon
      tabindex="0"
      focusable="true"
      type="${IconType.ICO_FULL_AVATAR}"
      width="${iconSize}"
      height="${iconSize}"
    ></slh-icon>`;
  }

  getSizeValue(size: Sizes): number {
    if (size === Sizes.XL) {
      return Number(this.possibleAvatarSizes.XL);
    }
    if (size === Sizes.LG) {
      return this.possibleAvatarSizes.LG;
    }
    if (size === Sizes.MD) {
      return this.possibleAvatarSizes.MD;
    }
    return this.possibleAvatarSizes.SM;
  }

  setAvatarSizes() {
    this.possibleAvatarSizes = SizeValues;
  }

  /**
   * @returns color hex value of user full name generated by color-hash library
   */
  getFullNameColor(): string {
    const colorHash = new ColorHash();
    let fullNameColor = "";
    const fullName = this.user?.fullName.trim();
    if (this.user?.color) {
      fullNameColor = this.user?.color;
    } else if (fullName) {
      fullNameColor = colorHash.hex(fullName);
    }
    return fullNameColor;
  }
}
